// BD Tracker card — replaces the Financial tracker on the pharmacy detail
// page when the app is in BD mode. Four counter cards + an attempt list
// showing title + date only. Rows are tappable to edit; long-press or menu
// to delete.
import React, { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fromYMD } from "@/lib/pp/dates";
import {
  listAttempts,
  deleteAttempt,
} from "@/lib/pp/bdApi";
import AttemptFormDialog from "@/components/pp/AttemptFormDialog";

export default function BDTracker({ pharmacyId }) {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editAttempt, setEditAttempt] = useState(null);

  const load = useCallback(async () => {
    if (!pharmacyId) return;
    setLoading(true);
    try {
      const rows = await listAttempts(pharmacyId);
      setAttempts(rows);
    } catch {
      toast.error("Couldn't load attempts");
    } finally {
      setLoading(false);
    }
  }, [pharmacyId]);

  useEffect(() => { load(); }, [load]);

  // A pharmacy has ONE current category, taken from the latest attempt.
  // Regardless of how many attempts exist, only that outcome bucket shows a
  // value (1); the other buckets stay at 0. Attempts total is the raw count.
  const currentOutcome = attempts.length > 0 ? attempts[0].outcome : null;
  const stats = {
    total: attempts.length,
    current: currentOutcome,
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete attempt "${row.title}"?`)) return;
    try {
      await deleteAttempt(row.id);
      toast.success("Attempt deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <Card data-testid="bd-tracker">
      <div className="flex items-center justify-between p-5 border-b border-[#E5E2DC]">
        <h3 className="text-xs tracking-[0.24em] uppercase font-semibold text-[#2D5A4C]">
          BD Tracker
        </h3>
        <Button
          size="sm"
          onClick={() => { setEditAttempt(null); setShowForm(true); }}
          data-testid="bd-tracker-add-attempt"
          className="bg-[#2E7D5B] hover:bg-[#25664A] gap-1"
        >
          <Plus className="h-3.5 w-3.5" /> Attempt
        </Button>
      </div>

      <CardContent className="pt-5 pb-5 px-5 space-y-4">
        {/* Counter cards — same visual size/padding/fonts as the Partners
            StatCard on the list page. Equal 2-column grid; the label uses a
            tighter letter-spacing so "Not Interested" stays on one line. */}
        <div
          className="grid gap-3.5"
          style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
        >
          <CounterCard label="Attempts" value={stats.total} tone="text-[#1C2B26]" testId="bd-count-attempts" sub="total attempts" />
          <CounterCard label="Pending" value={stats.current === "Pending" ? 1 : 0} tone="text-[#C77D26]" testId="bd-count-pending" sub="current outcome" />
          <CounterCard label="Interested" value={stats.current === "Interested" ? 1 : 0} tone="text-[#2E7D5B]" testId="bd-count-interested" sub="current outcome" />
          <CounterCard label="Not Interested" value={stats.current === "Not Interested" ? 1 : 0} tone="text-[#B4351B]" testId="bd-count-not-interested" sub="current outcome" />
        </div>

        {/* Attempt list — title + date only. */}
        <div className="space-y-2">
          {loading ? (
            <div className="text-sm text-[#6B7A73] py-2">Loading…</div>
          ) : attempts.length === 0 ? (
            <div className="text-sm text-[#6B7A73] py-2" data-testid="bd-tracker-empty">
              No attempts yet. Log your first outreach with <b>+ Attempt</b>.
            </div>
          ) : (
            attempts.map((a) => (
              <div
                key={a.id}
                data-testid={`bd-attempt-row-${a.id}`}
                className="group flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-[#E5E2DC] bg-white"
              >
                <button
                  type="button"
                  onClick={() => { setEditAttempt(a); setShowForm(true); }}
                  className="flex-1 text-left min-w-0"
                  data-testid={`bd-attempt-row-open-${a.id}`}
                >
                  <div className="font-semibold text-[15px] text-[#1C2B26] truncate">
                    {a.title || "—"}
                  </div>
                  <div className="text-xs text-[#6B7A73] mt-0.5">
                    {a.date ? format(fromYMD(a.date), "PPP") : "—"}
                  </div>
                </button>
                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition">
                  <button
                    type="button"
                    onClick={() => { setEditAttempt(a); setShowForm(true); }}
                    className="p-2 rounded hover:bg-[#F4F3EF] text-[#6B7A73]"
                    data-testid={`bd-attempt-edit-${a.id}`}
                    aria-label="Edit attempt"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(a)}
                    className="p-2 rounded hover:bg-[#F5DAD3] text-[#B4351B]"
                    data-testid={`bd-attempt-delete-${a.id}`}
                    aria-label="Delete attempt"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>

      <AttemptFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        pharmacyId={pharmacyId}
        attempt={editAttempt}
        onSaved={() => load()}
      />
    </Card>
  );
}

function CounterCard({ label, value, tone, testId, sub }) {
  return (
    <div
      className="bg-white border border-[#E5E2DC] rounded-[14px] flex flex-col justify-between text-left"
      style={{
        padding: "16px 18px",
        minHeight: 128,
        borderWidth: 1.5,
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      }}
      data-testid={testId}
    >
      <div
        className="text-[#2D5A4C] font-semibold uppercase"
        style={{
          fontSize: 12,
          letterSpacing: "0.14em",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>
      <div
        className={`${tone || ""}`}
        style={{
          fontFamily: "'Work Sans', system-ui, sans-serif",
          fontSize: 34,
          fontWeight: 700,
          margin: "8px 0",
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div className="text-[#6B7A73]" style={{ fontSize: 13 }}>{sub}</div>
    </div>
  );
}
